import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEye, FiClock } = FiIcons;

const EVENTSOURCE_OK = typeof EventSource !== 'undefined';
const RECONNECT_BASE_MS = 2000; // first transparent retry backoff
const RECONNECT_MAX_MS = 30_000; // capped, jittered backoff ceiling
const STALE_AFTER_MS = 75_000; // no frames for >2 keepalive cycles → HOST paused/gone

/**
 * ViewerStage — read-only live mirror for Viewer-Guests (T13).
 * Renders only the frames the HOST broadcasts. No data access of any kind:
 * a share is a live mirror, NOT a data grant.
 *
 * Stream resilience: the share hub (server/share-hub.js) is memory-only, so a
 * server restart or deploy mid-share invalidates viewer tokens (401 loop) and
 * briefly refuses connections (502). On stream loss we transparently
 * re-exchange the retained credentials (rate-limited server-side) and
 * reconnect with backoff; revoked/expired shares end the session instead.
 */
export default function ViewerStage({ companySlug, screenSlug }) {
  const [phase, setPhase] = useState('credentials'); // credentials|connecting|live|ended|error
  const [error, setError] = useState(null);
  const credsRef = useRef(null); // retained {username,password} for transparent re-auth
  const attemptRef = useRef(0); // reconnect backoff attempt; resets on successful open
  const frameRef = useRef(null);
  const [frameTick, setFrameTick] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const esRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const staleTimerRef = useRef(null);
  const disposedRef = useRef(false);
  const [stale, setStale] = useState(false); // no frames for a while → HOST paused/gone

  // Transient network loss (mid-share deploys, h2 GOAWAY): credentials stay
  // valid server-side until expiry/revocation, so silent re-auth + reconnect
  // is safe and keeps live calls unbroken from the viewer's perspective.
  const reconnect = useCallback(async () => {
    if (disposedRef.current || !credsRef.current) return;
    const attempt = attemptRef.current;
    setPhase((p) => (p === 'live' ? p : 'connecting'));
    try {
      const response = await fetch('/api/share/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credsRef.current.username,
          password: credsRef.current.password
        })
      });
      const body = await response.json().catch(() => ({}));
      // 401 = revoked/expired/wrong password → the share is genuinely over.
      if (response.status === 401 || body?.error?.code === 'SHARE_REVOKED' || body?.error?.code === 'SHARE_EXPIRED') {
        setPhase('ended');
        return;
      }
      if (!response.ok) throw new Error(body?.error?.message || 're-auth failed');
      connectSse(body.token, body.shareId);
    } catch {
      attemptRef.current = attempt + 1;
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt) + Math.random() * 500;
      reconnectTimerRef.current = setTimeout(reconnect, delay);
    }
     
  }, []);

  function connectSse(token, shareId) {
    const es = EVENTSOURCE_OK
      ? new EventSource(`/api/share/${encodeURIComponent(shareId)}/stream?token=${encodeURIComponent(token)}`)
      : null;

    if (!es) {
      setError('This browser cannot stream live shares.');
      setPhase('error');
      return;
    }

    es.addEventListener('frame', (event) => {
      try {
        frameRef.current = JSON.parse(event.data);
        setFrameTick((t) => t + 1);
      } catch { /* malformed frame — wait for the next one */ }
      setPhase('live');
      // A fresh frame means the HOST is actively broadcasting — clear the
      // paused/stale flag and re-arm the watchdog.
      setStale(false);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      staleTimerRef.current = setTimeout(() => setStale(true), STALE_AFTER_MS);
    });

    es.addEventListener('share-ended', () => {
      es.close();
      setPhase('ended');
    });

    es.onopen = () => {
      attemptRef.current = 0; // healthy stream — reset the backoff ladder
      setPhase((p) => (p === 'live' ? p : 'connecting'));
    };
    es.onerror = () => {
      // EventSource auto-reconnects while CONNECTING; a CLOSED stream means
      // the server rejected us (token invalidated by a restart/deploy) —
      // re-exchange credentials and come back transparently.
      es.close();
      if (disposedRef.current) return;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(reconnect, RECONNECT_BASE_MS);
    };

    esRef.current = es;
    setPhase('connecting');
  }

  async function join(event) {
    event?.preventDefault();
    setPhase('connecting');
    setError(null);
    try {
      const response = await fetch('/api/share/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error?.message || 'Invalid share credentials.');

      credsRef.current = { username: username.trim(), password };
      frameRef.current = null;
      setFrameTick((t) => t + 1);
      connectSse(body.token, body.shareId, body.screenSlug);
    } catch (err) {
      setError(err.message || 'Invalid share credentials.');
      setPhase('credentials');
    }
  }

  useEffect(() => () => {
    disposedRef.current = true;
    if (esRef.current) esRef.current.close();
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
  }, []);

  function Again() {
    setPhase('credentials');
    setError(null);
  }

  if (phase === 'credentials' || phase === 'error' || phase === 'ended') {
    return (
      <main className="auth-page">
        <section className="auth-card">
          <span className="auth-logo"><SafeIcon icon={FiEye} /></span>
          <span className="eyebrow">Live share</span>
          {phase === 'ended' ? (
            <>
              <h1>This share has ended.</h1>
              <p className="muted">The HOST stopped sharing or the session expired. Nothing is stored on your side.</p>
              <button type="button" className="button primary" onClick={Again}>Join another share</button>
            </>
          ) : phase === 'error' ? (
            <>
              <h1>Connection lost.</h1>
              <p className="muted">{error || 'The live stream was interrupted.'}</p>
              <button type="button" className="button primary" onClick={Again}>Try again</button>
            </>
          ) : (
            <>
              <h1>You&rsquo;re watching a live share.</h1>
              <p>
                {companySlug && screenSlug
                  ? <>A presenter is sharing <b>{screenSlug}</b> from <b>{companySlug}</b>.</>
                  : <>A presenter is sharing their workspace with you live.</>}
              </p>
              <form onSubmit={join}>
                <label>Share username
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. share-foxclub-42"
                    autoComplete="off"
                    required
                  />
                </label>
                <label>Password
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="e.g. fox-club-wine"
                    autoComplete="off"
                    required
                  />
                </label>
                {error && <div className="form-error">{error}</div>}
                <button className="button primary" type="submit" disabled={phase === 'connecting'}>
                  <SafeIcon icon={FiEye} />
                  Join live share
                </button>
              </form>
              <small className="form-hint">Viewers see only what the HOST broadcasts — never workspace access.</small>
            </>
          )}
        </section>
      </main>
    );
  }

  const frame = frameRef.current;

  return (
    <div className="viewer-stage">
      <header className="topbar">
        <div>
          <small>Live share</small>
          <b>{companySlug || 'Workspace'}</b>
        </div>
        <span className="viewer-live-chip">
          <span className={`viewer-live-dot ${stale ? 'stale' : ''}`} />
          {phase === 'live' ? (stale ? 'Paused' : 'Live') : 'Connecting…'}
        </span>
      </header>

      <div className="viewer-canvas">
        {!frame ? (
          <div className="viewer-empty">
            <SafeIcon icon={FiClock} />
            <p>{stale ? 'The HOST has paused sharing.' : 'Waiting for the HOST to share their screen…'}</p>
          </div>
        ) : (
          <div className="viewer-frame">
            <span className="eyebrow">{frame.screenSlug || screenSlug || 'workspace'}</span>
            {stale && <span className="viewer-stale-note">Mirror paused by the HOST</span>}
            <ViewerPayload payload={frame.payload} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Minimal read-only frame renderer. Frames are pre-rendered HOST state —
 * nothing here computes, fetches, or mutates. Unknown kinds fall back to a
 * generic JSON mirror (still read-only).
 */
function ViewerPayload({ payload }) {
  const items = useMemo(() => {
    if (!payload) return [];
    if (payload.kind === 'overview') {
      return [{ kind: 'overview', text: `${payload.business || 'Workspace'} — ${payload.screen || 'workspace'}` }];
    }
    if (Array.isArray(payload?.messages)) {
      return payload.messages.map((m) => ({ kind: m.role === 'user' ? 'user' : 'bot', text: m.content || '' }));
    }
    if (Array.isArray(payload?.blocks)) {
      return payload.blocks.map((b) => ({ kind: 'block', text: `${b.type || 'block'}: ${b.copy || b.text || ''}` }));
    }
    return [{ kind: 'json', text: JSON.stringify(payload, null, 2) }];
  }, [payload]);

  if (!payload) return null;

  return (
    <div className="viewer-payload">
      {items.map((item, idx) => (
        <div key={idx} className={`viewer-bubble ${item.kind}`}>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  );
}
